import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { inboxApi, type InboxMessage } from '../api/inboxApi';
import { InboxAttachment } from '../components/InboxAttachment';
import { InboxComposer } from '../components/InboxComposer';
import type { InboxBotSummary, InboxConversationSummary, InboxPersonSummary } from '@workspace/types';
import { toast } from 'react-toastify';

const CHANNEL_LABEL: Record<string, string> = {
  WEB: 'Web',
  WHATSAPP: 'WhatsApp',
  TELEGRAM: 'Telegram',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
};

function channelBadge(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

function isWhatsAppWindowOpen(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) {
    return true;
  }
  return new Date(expiresAt).getTime() > Date.now();
}

function senderLabel(sender: InboxMessage['sender'], contextContent?: string | null): string {
  if (sender === 'OPERATOR') {
    return 'Operador';
  }
  if (contextContent === 'SYSTEM_RESUME') {
    return 'Sistema';
  }
  return sender === 'USER' ? 'Cliente' : 'Bot';
}

function inboxShowsTicketFilters(
  inboxes: InboxBotSummary[],
  selectedBotId: string | null,
): boolean {
  if (selectedBotId === null) {
    return inboxes.some(inbox => inbox.hasHelpdeskAgent);
  }
  return inboxes.some(inbox => inbox.botId === selectedBotId && inbox.hasHelpdeskAgent);
}

const TICKET_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'OPEN', label: 'Abiertos' },
  { value: 'IN_PROGRESS', label: 'En curso' },
  { value: 'CLOSED', label: 'Cerrados' },
];

function TicketStatusFilters({
  value,
  onChange,
}: {
  value: string;
  onChange: (status: string) => void;
}) {
  return (
    <div className='flex flex-wrap gap-1 border-b p-2'>
      {TICKET_FILTERS.map(filter => (
        <button
          key={filter.value || 'all'}
          type='button'
          className={`rounded px-2 py-1 text-xs ${
            value === filter.value ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}
          onClick={() => onChange(filter.value)}
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
}

export function InboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inboxes, setInboxes] = useState<InboxBotSummary[]>([]);
  const [conversations, setConversations] = useState<InboxConversationSummary[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    searchParams.get('conversationId'),
  );
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof inboxApi.getConversation>> | null>(
    null,
  );
  const [person, setPerson] = useState<InboxPersonSummary | null>(null);
  const [reply, setReply] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [ticketFilter, setTicketFilter] = useState<string>('');
  const [error, setError] = useState('');

  const selectedConversation = useMemo(
    () => conversations.find(item => item.id === selectedConversationId) ?? null,
    [conversations, selectedConversationId],
  );
  const showTicketFilters = inboxShowsTicketFilters(inboxes, selectedBotId);

  const loadInboxes = useCallback(async () => {
    setInboxes(await inboxApi.listInboxes());
  }, []);

  const loadConversations = useCallback(async () => {
    const result = await inboxApi.listConversations({
      botId: selectedBotId ?? undefined,
      ticketStatus: ticketFilter || undefined,
    });
    setConversations(result.items);
  }, [selectedBotId, ticketFilter]);

  const loadThread = useCallback(async (conversationId: string) => {
    const [threadMessages, threadDetail] = await Promise.all([
      inboxApi.listMessages(conversationId),
      inboxApi.getConversation(conversationId),
    ]);
    setMessages(threadMessages.filter(message => message.contextContent !== 'SYSTEM_RESUME'));
    setDetail(threadDetail);
    if (threadDetail.personId) {
      const personDetail = await inboxApi.getPerson(threadDetail.personId);
      setPerson(personDetail);
    } else {
      setPerson(null);
    }
  }, []);

  useEffect(() => {
    void loadInboxes().catch(err => setError(err instanceof Error ? err.message : 'Error'));
  }, [loadInboxes]);

  useEffect(() => {
    void loadConversations().catch(err => setError(err instanceof Error ? err.message : 'Error'));
  }, [loadConversations]);

  useEffect(() => {
    if (!showTicketFilters && ticketFilter) {
      setTicketFilter('');
    }
  }, [showTicketFilters, ticketFilter]);

  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      setDetail(null);
      return undefined;
    }
    void loadThread(selectedConversationId).catch(err =>
      setError(err instanceof Error ? err.message : 'Error'),
    );
    const timer = window.setInterval(() => {
      void loadThread(selectedConversationId).catch(() => undefined);
      void loadConversations().catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [selectedConversationId, loadThread, loadConversations]);

  const selectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setSearchParams({ conversationId });
  };

  const canCompose =
    detail
    && ['WEB', 'WHATSAPP', 'TELEGRAM', 'FACEBOOK', 'INSTAGRAM'].includes(detail.channel)
    && (detail.channel !== 'WHATSAPP' || isWhatsAppWindowOpen(detail.windowExpiresAt));
  const textOnlyChannel = detail?.channel === 'FACEBOOK' || detail?.channel === 'INSTAGRAM';

  const onSend = async () => {
    if (!selectedConversationId || (!reply.trim() && !attachment)) {
      return;
    }
    if (textOnlyChannel && attachment) {
      setError('Este canal solo admite texto por ahora');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (attachment) {
        await inboxApi.replyWithFile(selectedConversationId, attachment, reply.trim() || undefined);
        setAttachment(null);
      } else {
        await inboxApi.reply(selectedConversationId, reply.trim());
      }
      setReply('');
      await loadThread(selectedConversationId);
      await loadConversations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo enviar';
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const onCloseTicket = async () => {
    if (!selectedConversationId) {
      return;
    }
    setBusy(true);
    try {
      const result = await inboxApi.closeTicket(selectedConversationId);
      toast.success(`Ticket #${result.number} cerrado`);
      await loadThread(selectedConversationId);
      await loadConversations();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo cerrar el ticket');
    } finally {
      setBusy(false);
    }
  };

  const onResume = async () => {
    if (!selectedConversationId) {
      return;
    }
    setBusy(true);
    try {
      await inboxApi.resume(selectedConversationId);
      await loadThread(selectedConversationId);
      await loadConversations();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo restablecer el bot');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='flex h-[calc(100vh-5rem)] min-h-[32rem] gap-3'>
      <aside className='w-56 shrink-0 overflow-y-auto rounded-lg border bg-card p-2'>
        <p className='px-2 py-1 text-xs font-semibold uppercase text-muted-foreground'>Casillas</p>
        <button
          type='button'
          className={`mb-1 w-full rounded px-2 py-2 text-left text-sm ${
            selectedBotId === null ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
          }`}
          onClick={() => {
            setSelectedBotId(null);
            setSelectedConversationId(null);
            setSearchParams({});
            if (!inboxes.some(item => item.hasHelpdeskAgent)) {
              setTicketFilter('');
            }
          }}
        >
          Unificado
        </button>
        {inboxes.map(inbox => (
          <button
            key={inbox.botId}
            type='button'
            className={`mb-1 w-full rounded px-2 py-2 text-left text-sm ${
              selectedBotId === inbox.botId ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
            onClick={() => {
              setSelectedBotId(inbox.botId);
              setSelectedConversationId(null);
              setSearchParams({});
              if (!inbox.hasHelpdeskAgent) {
                setTicketFilter('');
              }
            }}
          >
            <div className='font-medium'>{inbox.botName}</div>
            <div className='text-xs opacity-80'>
              {inbox.openCount} hilos · {inbox.humanActiveCount} humano
            </div>
          </button>
        ))}
      </aside>

      <section className='w-80 shrink-0 overflow-y-auto rounded-lg border bg-card'>
        {showTicketFilters ? (
          <TicketStatusFilters value={ticketFilter} onChange={setTicketFilter} />
        ) : null}
        {conversations.map(conversation => (
          <button
            key={conversation.id}
            type='button'
            className={`block w-full border-b px-3 py-2 text-left hover:bg-muted ${
              selectedConversationId === conversation.id ? 'bg-muted' : ''
            }`}
            onClick={() => selectConversation(conversation.id)}
          >
            <div className='flex items-center justify-between gap-2'>
              <span className='truncate text-sm font-medium'>{conversation.userLabel}</span>
              <span className='shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px]'>
                {channelBadge(conversation.channel)}
              </span>
            </div>
            <div className='truncate text-xs text-muted-foreground'>
              {conversation.botName} · {conversation.handoffStatus === 'HUMAN_ACTIVE' ? 'Humano' : 'Bot'}
              {conversation.ticket ? ` · #${conversation.ticket.number} ${conversation.ticket.status}` : ''}
            </div>
            <div className='truncate text-xs'>{conversation.lastMessagePreview}</div>
          </button>
        ))}
      </section>

      <section className='flex min-w-0 flex-1 flex-col rounded-lg border bg-card'>
        {!selectedConversation ? (
          <div className='flex flex-1 items-center justify-center text-sm text-muted-foreground'>
            Seleccioná una conversación
          </div>
        ) : (
          <>
            <div className='border-b px-4 py-3'>
              <div className='flex items-center justify-between gap-2'>
                <div>
                  <h2 className='text-lg font-semibold'>{selectedConversation.userLabel}</h2>
                  <p className='text-sm text-muted-foreground'>
                    {selectedConversation.botName} · {channelBadge(selectedConversation.channel)}
                  </p>
                </div>
                <div className='flex gap-2'>
                  {detail?.ticket ? (
                    <span className='rounded bg-muted px-2 py-1 text-xs font-medium'>
                      #{detail.ticket.number} · {detail.ticket.status}
                    </span>
                  ) : null}
                  {detail?.ticket && detail.ticket.status !== 'CLOSED' ? (
                    <Button variant='outline' size='sm' disabled={busy} onClick={() => void onCloseTicket()}>
                      Cerrar ticket
                    </Button>
                  ) : null}
                  {detail?.handoffStatus === 'HUMAN_ACTIVE' ? (
                    <Button variant='outline' size='sm' disabled={busy} onClick={() => void onResume()}>
                      Restablecer bot
                    </Button>
                  ) : null}
                </div>
              </div>
              {detail?.handoffStatus === 'HUMAN_ACTIVE' ? (
                <p className='mt-2 text-sm text-amber-700'>
                  El bot está en pausa. Tus mensajes interrumpen la automatización.
                </p>
              ) : null}
              {detail?.channel === 'WHATSAPP' && !isWhatsAppWindowOpen(detail.windowExpiresAt) ? (
                <p className='mt-2 text-sm text-amber-700'>
                  La ventana de 24 h de WhatsApp está cerrada. No podés enviar texto libre hasta que el
                  cliente responda.
                </p>
              ) : null}
            </div>

            <div className='flex-1 space-y-2 overflow-y-auto p-4'>
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    message.sender === 'USER'
                      ? 'bg-muted'
                      : message.sender === 'OPERATOR'
                        ? 'ml-auto bg-blue-100 text-blue-950'
                        : 'ml-auto bg-primary/10'
                  }`}
                >
                  <div className='mb-1 text-[11px] font-medium opacity-70'>
                    {senderLabel(message.sender, message.contextContent)}
                  </div>
                  <InboxAttachment message={message} />
                </div>
              ))}
            </div>

            <InboxComposer
              reply={reply}
              attachment={attachment}
              canCompose={!!canCompose}
              allowAttachments={!textOnlyChannel}
              busy={busy}
              error={error}
              onReplyChange={setReply}
              onAttachmentChange={setAttachment}
              onError={message => {
                setError(message);
                toast.error(message);
              }}
              onSend={() => void onSend()}
            />
          </>
        )}
      </section>

      {person ? (
        <aside className='w-72 shrink-0 overflow-y-auto rounded-lg border bg-card p-3'>
          <h3 className='text-sm font-semibold'>Persona</h3>
          <p className='text-sm text-muted-foreground'>{person.displayName || 'Sin nombre'}</p>
          <div className='mt-3 space-y-1'>
            {person.identities.map(identity => (
              <div key={identity.id} className='text-xs'>
                <span className='font-medium'>{identity.kind}</span>: {identity.value}
              </div>
            ))}
          </div>
          <div className='mt-4 space-y-3'>
            {person.conversationsByBot.map(group => (
              <div key={group.botId}>
                <p className='text-xs font-semibold uppercase text-muted-foreground'>{group.botName}</p>
                {group.conversations.map(conversation => (
                  <button
                    key={conversation.id}
                    type='button'
                    className='mt-1 block w-full rounded px-2 py-1 text-left text-xs hover:bg-muted'
                    onClick={() => selectConversation(conversation.id)}
                  >
                    {channelBadge(conversation.channel)} · {conversation.lastMessagePreview}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>
      ) : null}
    </div>
  );
}
